import React, { useMemo } from 'react';
import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { VIEWER_HOOKS } from '@wix/pricing-plans-common/hooks';
import { getVariantPrice } from '@wix/pricing-plans-utils';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { Image, ImageLoadingBehaviorOptions, ImageResizeOptions } from '@wix/wix-ui-tpa/cssVars';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { ButtonDemo } from '../../../../../constants/settings-events';
import { demoImages } from '../../../../../fixtures';
import { alignmentName } from '../../../../../utils/alignment';
import { planImageToCoverImage, getPlanBenefits } from '../../../../../utils/plan-v3-utils';
import settingsParams, { ButtonStyle } from '../../../settingsParams';
import stylesParams, { type TopButton } from '../../../stylesParams';
import { Footer } from './Footer';
import { FreeTrial } from './FreeTrial';
import { st, classes, vars } from './index.st.css';
import { PlanButtons } from './PlanButtons';
import { PlanName } from './PlanName';
import { Price } from './Price';
import { Recurrence } from './Recurrence';
import { Ribbon } from './Ribbon';
import { SetupFee } from './SetupFee';
import { Tagline } from './Tagline';
import { Validity } from './Validity';

export interface PlanProps {
  plan: Plan;
  variant: PricingVariant | undefined;
  buttonDemo: ButtonDemo;
  recurringPlansExist: boolean;
  freeTrialDaysExist: boolean;
  taglinesExist: boolean;
  setupFeesExist: boolean;
  validityCyclesExist: boolean;
  highlighted: boolean;
  anyBenefits?: boolean;
  expandMobileBenefits: boolean;
  onBuyNowClick: () => void;
  onAddToCartClick: () => void;
  isGoToCheckoutLoading: boolean;
  isAddToCartLoading?: boolean;
  isOnEcom?: boolean;
}

export const PlanCard: React.FC<PlanProps> = ({
  plan,
  variant,
  recurringPlansExist,
  freeTrialDaysExist,
  validityCyclesExist,
  taglinesExist,
  setupFeesExist,
  highlighted,
  buttonDemo,
  onBuyNowClick,
  onAddToCartClick,
  anyBenefits,
  expandMobileBenefits,
  isGoToCheckoutLoading,
  isAddToCartLoading,
  isOnEcom,
}) => {
  const settings = useSettings();
  const styles = useStyles();
  const { isMobile } = useEnvironment();
  const shadowStyle = useShadowStyle(highlighted);
  const hasTwoDetails = validityCyclesExist && freeTrialDaysExist;
  const blendBgColors = !settings.get(settingsParams.wereNewSettingsOpened);
  const descriptionAlignment = alignmentName(styles.get(stylesParams.descriptionAlignment));
  const primaryButtonAlignment = alignmentName(
    styles.get(buttonAlignmentStylesParam(styles.get(stylesParams.buttonStyle))),
  );
  const secondaryButtonAlignment = alignmentName(styles.get(stylesParams.secondaryButtonAlignment));
  const showPlanTagline = taglinesExist && styles.get(stylesParams.showTagline);
  const displayedButtons = styles.get(stylesParams.displayedButtons);
  const topButton = styles.get(stylesParams.topButton) as TopButton;

  const coverImage = planImageToCoverImage(plan.image);
  const DEFAULT_IMAGE_SRC = demoImages.default.uri;
  const showCoverImage = styles.get(stylesParams.showImage) || settings.get(settingsParams.showImage);
  const price = getVariantPrice(variant!, plan.currency);
  const benefits = getPlanBenefits(plan);

  return (
    <div
      className={st(classes.planWrapper, {
        showShadow: shadowStyle.show,
        highlighted,
      })}
      style={{ [vars.shadowXOffset]: shadowStyle.xOffset + 'px', [vars.shadowYOffset]: shadowStyle.yOffset + 'px' }}
    >
      <Ribbon highlighted={highlighted} />
      <div
        className={st(classes.plan, {
          highlighted,
          blendBgColors,
        })}
      >
        {showCoverImage ? (
          <div className={classes.coverImageContainer}>
            <Image
              data-hook={VIEWER_HOOKS.COVER_IMAGE}
              alt={coverImage?.altText ?? ''}
              src={coverImage?.uri ?? DEFAULT_IMAGE_SRC}
              resize={ImageResizeOptions.cover}
              loadingBehavior={ImageLoadingBehaviorOptions.blur}
            />
          </div>
        ) : null}
        <div className={st(classes.planTop, { textAlignment: descriptionAlignment })}>
          <PlanName planId={plan.id ?? ''} highlighted={highlighted} name={plan.name || ''} />
          <Price highlighted={highlighted} price={price} />
          <Recurrence
            highlighted={highlighted}
            plan={plan}
            variant={variant}
            recurringPlansExist={recurringPlansExist}
          />
          {setupFeesExist && <SetupFee plan={plan} variant={variant} planId={plan.id!} highlighted={highlighted} />}
          {showPlanTagline && (
            <Tagline planId={plan.id ?? ''} highlighted={highlighted} description={plan.description || ''} />
          )}
          <div className={st(classes.duration, { hasTwoDetails, mobile: isMobile })}>
            <Validity highlighted={highlighted} variant={variant} />
            <FreeTrial highlighted={highlighted} variant={variant} freeTrialDaysExist={freeTrialDaysExist} />
          </div>
          <PlanButtons
            plan={plan}
            variant={variant}
            highlighted={highlighted}
            buttonDemo={buttonDemo}
            isGoToCheckoutLoading={isGoToCheckoutLoading}
            onBuyNowClick={onBuyNowClick}
            isAddToCartLoading={isAddToCartLoading}
            onAddToCartClick={onAddToCartClick}
            isOnEcom={isOnEcom}
            displayedButtons={displayedButtons}
            topButton={topButton}
            primaryButtonAlignment={primaryButtonAlignment}
            secondaryButtonAlignment={secondaryButtonAlignment}
          />
        </div>
        <Footer
          benefits={benefits}
          anyBenefits={anyBenefits}
          highlighted={highlighted}
          expandMobileBenefits={expandMobileBenefits}
        />
      </div>
    </div>
  );
};

const buttonAlignmentStylesParamMap = {
  [ButtonStyle.EMPTY_CORNERED]: stylesParams.buttonEmptyCorneredAlignment,
  [ButtonStyle.EMPTY_ROUNDED]: stylesParams.buttonEmptyRoundedAlignment,
  [ButtonStyle.FULL_ROUNDED]: stylesParams.buttonFullRoundedAlignment,
  [ButtonStyle.FULL_CORNERED]: stylesParams.buttonFullCorneredAlignment,
};

const buttonAlignmentStylesParam = (value: ButtonStyle) => {
  return buttonAlignmentStylesParamMap[value];
};

export const useShadowStyle = (highlighted: boolean) => {
  const settings = useSettings();
  const styles = useStyles();

  return useMemo(() => {
    const showShadowParam = highlighted ? 'showHighlightedPlanCardShadow' : 'showRegularPlanCardShadow';
    const shadowDistanceParam = highlighted ? 'highlightedCardShadowDistance' : 'regularCardShadowDistance';
    const shadowAngleParam = highlighted ? 'highlightedCardShadowAngle' : 'regularCardShadowAngle';
    const shadowDistance = styles.get(stylesParams[shadowDistanceParam]);
    const showShadow = settings.get(settingsParams[showShadowParam]);
    const shadowAngle = styles.get(stylesParams[shadowAngleParam]);
    const shadowXOffset = -Math.round(shadowDistance * Math.sin((shadowAngle * Math.PI) / 180));
    const shadowYOffset = Math.round(shadowDistance * Math.cos((shadowAngle * Math.PI) / 180));

    return {
      show: showShadow,
      xOffset: shadowXOffset,
      yOffset: shadowYOffset,
    };
  }, [settings, styles, highlighted]);
};
